/**
 * WP-C1 — Authenticated server entry points for proposal canonicalization.
 *
 * The actor is NEVER accepted from the caller: `requireSupabaseAuth` puts the
 * verified `userId` on the context. The privileged adapter is imported INSIDE
 * each handler so the service-role client never enters a client bundle.
 *
 * Guest-first access: an anonymous guest may converse, read and even edit an
 * advisory proposal's draft, but a canonical Loop is durable account-owned
 * state. Canonicalization is refused for anonymous identities here, at the
 * authority boundary, regardless of what the UI offered.
 */

import { createServerFn } from "@tanstack/react-start";

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import {
  canonicalizeProposalCommand,
  editProposalCommand,
  type CanonicalizationOutcome,
  type ProposalEditOutcome,
} from "./canonicalizationCommand";
import {
  validateCanonicalizeProposalInput,
  validateEditProposalInput,
} from "./canonicalizationRequest";

function isAnonymous(claims: unknown): boolean {
  return (claims as { is_anonymous?: unknown }).is_anonymous === true;
}

/**
 * Transport validation runs inside the handler so a malformed body is answered
 * with the categorical `validation_failed` outcome (nothing written, no raw
 * error crossing the RPC boundary) instead of an unhandled 500.
 */
function parse<T>(validate: (raw: unknown) => T, raw: unknown): T | null {
  try {
    return validate(raw);
  } catch {
    return null;
  }
}

const VALIDATION_FAILED = { ok: false, reason: "validation_failed", retryable: false } as const;

export const editHostedProposal = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((raw: unknown) => raw)
  .handler(async ({ data: raw, context }): Promise<ProposalEditOutcome> => {
    const data = parse(validateEditProposalInput, raw);
    if (!data) return VALIDATION_FAILED;
    const { supabaseCanonicalizationPort } = await import("./canonicalizationAdapter.server");
    return editProposalCommand(supabaseCanonicalizationPort, { userId: context.userId }, data);
  });

export const canonicalizeHostedProposal = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((raw: unknown) => raw)
  .handler(async ({ data: raw, context }): Promise<CanonicalizationOutcome> => {
    const data = parse(validateCanonicalizeProposalInput, raw);
    if (!data) return VALIDATION_FAILED;
    if (isAnonymous(context.claims)) {
      return { ok: false, reason: "role_not_permitted", retryable: false };
    }
    const { supabaseCanonicalizationPort } = await import("./canonicalizationAdapter.server");
    return canonicalizeProposalCommand(
      supabaseCanonicalizationPort,
      { userId: context.userId },
      data,
    );
  });
